import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Search, Train, Camera, Map as MapIcon, ChevronRight, Menu, Info, Sparkles, Lock, Radio, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import DepotMap from "@/components/DepotMap";
import { DepotSeal } from "@/components/DepotSeal";
import { FeaturedTrips } from "@/components/FeaturedTrips";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero images cycle between depots and signal/interlocking towers
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1762027628224-f336117844f0?auto=format&fit=crop&w=2000&q=80",
    type: "depot",
    label: "Historic Passenger Depots",
    credit: null,
  },
  {
    url: "https://upload.wikimedia.org/wikipedia/commons/a/a3/Harris_Switch_Tower.jpg",
    type: "tower",
    label: "Interlocking Towers",
    credit: "Harris Tower · Harrisburg, PA · photo Niagara · CC BY 3.0",
  },
  {
    url: "https://upload.wikimedia.org/wikipedia/commons/e/ed/Des_Plaines_interlocking_tower.jpg",
    type: "tower",
    label: "Signal Stations of a Forgotten Era",
    credit: "Des Plaines Tower · photo Slambo · CC BY-SA 2.5",
  },
];

export default function Home() {
  const [depots, setDepots] = useState([]);
  const [towers, setTowers] = useState([]);
  const [states, setStates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [onlyVisitable, setOnlyVisitable] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showDepots, setShowDepots] = useState(true);
  const [showTowers, setShowTowers] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const navigate = useNavigate();

  // Cycle hero image every 7 seconds
  useEffect(() => {
    const id = setInterval(() => setHeroIndex((i) => (i + 1) % HERO_IMAGES.length), 7000);
    return () => clearInterval(id);
  }, []);

  const currentHero = HERO_IMAGES[heroIndex];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (searchQuery) params.append("search", searchQuery);
      if (selectedState !== "all") params.append("state", selectedState);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (onlyVisitable) params.append("visitable", "true");
      params.append("limit", "10000");

      const towerParams = new URLSearchParams();
      if (searchQuery) towerParams.append("search", searchQuery);
      if (selectedState !== "all") towerParams.append("state", selectedState);
      towerParams.append("limit", "2000");

      const [depotsRes, towersRes, statesRes, statsRes] = await Promise.all([
        axios.get(`${API}/depots?${params.toString()}`),
        axios.get(`${API}/towers?${towerParams.toString()}`),
        axios.get(`${API}/states`),
        axios.get(`${API}/stats`)
      ]);
      
      setDepots(depotsRes.data);
      setTowers(towersRes.data);
      setStates(statesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedState, selectedStatus, onlyVisitable]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleDepotClick = (depot) => {
    navigate(`/depot/${depot.id}`);
  };

  const handleTowerClick = (tower) => {
    navigate(`/tower/${tower.id}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header — museum / archive brand */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-4 group" data-testid="logo-link">
              <DepotSeal
                className="text-amber-500/90 group-hover:text-amber-400 transition-colors shrink-0"
                size={54}
              />
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-[11px] text-amber-500 uppercase tracking-[0.32em] font-semibold mb-1.5">
                  A Preservation Archive
                </span>
                <span className="font-serif text-[22px] font-semibold tracking-tight text-white leading-none">
                  North American Depots<span className="text-amber-500"> &amp; </span>Towers
                </span>
                <span className="text-[12px] text-white/70 mt-2 tracking-normal">
                  Hosted by <span className="text-white font-medium">Railstream</span>
                  <span className="mx-1.5 text-white/30">·</span>
                  <span className="italic text-white/60">in memoriam</span>{" "}
                  <span className="text-white/85">Jack Marshall</span>
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7">
              <Link
                to="/"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-map"
              >
                Map
              </Link>
              <Link
                to="/states"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-states"
              >
                Regions
              </Link>
              <Link
                to="/about"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-about"
              >
                About
              </Link>
              <Link
                to="/submit"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-submit"
              >
                Contribute
              </Link>
              <Link
                to="/trip-planner"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-trip-planner"
              >
                Plan Trip
              </Link>
              <Link
                to="/contributors"
                className="text-[13px] uppercase tracking-[0.15em] text-white/85 hover:text-amber-500 transition-colors"
                data-testid="nav-contributors"
              >
                Contributors
              </Link>
              <span className="w-px h-4 bg-white/20" aria-hidden="true" />
              <Link
                to="/admin"
                className="text-[13px] uppercase tracking-[0.15em] text-white/60 hover:text-amber-500 transition-colors"
                data-testid="nav-admin"
              >
                Sign in
              </Link>
            </nav>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-[#111] border-white/10">
                <div className="flex items-center gap-3 mb-8">
                  <DepotSeal size={44} className="text-amber-500 shrink-0" showEst={false} />
                  <div>
                    <span className="font-serif font-bold block text-white leading-tight">
                      North American Depots &amp; Towers
                    </span>
                    <span className="text-xs text-amber-500">Hosted by Railstream</span>
                  </div>
                </div>
                <nav className="flex flex-col gap-2">
                  <Link 
                    to="/" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <MapIcon className="w-5 h-5" />
                    <span className="font-medium">Map</span>
                  </Link>
                  <Link 
                    to="/states" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <MapPin className="w-5 h-5" />
                    <span className="font-medium">Browse Regions</span>
                  </Link>
                  <Link 
                    to="/about" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Info className="w-5 h-5" />
                    <span className="font-medium">About</span>
                  </Link>
                  <Link
                    to="/submit"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <MapPin className="w-5 h-5" />
                    <span className="font-medium">Submit a Location</span>
                  </Link>
                  <Link
                    to="/trip-planner"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <MapIcon className="w-5 h-5" />
                    <span className="font-medium">Plan a Trip</span>
                  </Link>
                  <Link 
                    to="/admin" 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-amber-500 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Lock className="w-5 h-5" />
                    <span className="font-medium">Admin</span>
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {/* Sub-rail: stats plate */}
        {stats && (
          <div className="hidden md:block border-t border-white/10 bg-black/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-x-8 gap-y-1 flex-wrap py-2.5 text-[12px] uppercase tracking-[0.14em] text-white/75">
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="font-mono text-amber-500 font-semibold text-[14px]">
                    {stats.total_depots.toLocaleString()}
                  </span>
                  Depots
                </span>
                <span className="text-white/30" aria-hidden="true">·</span>
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="font-mono text-amber-500 font-semibold text-[14px]">
                    {(stats.total_towers || 0).toLocaleString()}
                  </span>
                  Interlocking Towers
                </span>
                <span className="text-white/30" aria-hidden="true">·</span>
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="font-mono text-white/85 text-[14px]">
                    {stats.states_count}
                  </span>
                  Regions
                </span>
                <span className="text-white/30" aria-hidden="true">·</span>
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="font-mono text-white/85 text-[14px]">
                    {(stats.total_photos || 0).toLocaleString()}
                  </span>
                  Photographs
                </span>
                <span className="ml-auto hidden xl:inline text-white/55 normal-case tracking-normal italic text-[13px] font-serif">
                  A collaborative catalog of North American railroad architecture
                </span>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section — cycling between depots and towers */}
      <section
        className="relative h-[70vh] md:h-[80vh] bg-cover bg-center flex items-end overflow-hidden"
        data-testid="hero-section"
      >
        {HERO_IMAGES.map((img, i) => (
          <div
            key={img.url}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-in-out"
            style={{
              backgroundImage: `url('${img.url}')`,
              opacity: i === heroIndex ? 1 : 0,
            }}
            aria-hidden={i !== heroIndex}
          />
        ))}
        <div className="absolute inset-0 hero-overlay" />

        {/* Decorative blurs */}
        <div className="absolute top-1/4 left-10 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/3 right-20 w-40 h-40 bg-orange-600/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}} />

        <div className="relative z-10 w-full pb-16 md:pb-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4 animate-fade-in-up">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span
                key={currentHero.label}
                className="text-amber-500 text-sm font-semibold tracking-wider uppercase animate-fade-in-up"
                data-testid="hero-label"
              >
                {currentHero.label}
              </span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight mb-6 animate-fade-in-up stagger-1 text-white">
              North American
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-500 to-red-500">
                Depots & Towers
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-white/70 mb-10 animate-fade-in-up stagger-2 max-w-2xl">
              Explore <span className="text-amber-500 font-bold">9,000+</span> historic passenger depots and{" "}
              <span className="text-amber-500 font-bold">1,400+</span> railroad signal &amp; interlocking towers across North America
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Button
                size="lg"
                className="h-14 px-8 text-lg bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all animate-pulse-glow"
                onClick={() => document.getElementById('map-section')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="explore-map-btn"
              >
                <MapIcon className="w-5 h-5 mr-2" />
                Explore the Map
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-14 px-8 text-lg bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-amber-500/50"
                onClick={() => navigate('/states')}
                data-testid="browse-states-btn"
              >
                Browse by Region
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Image-cycler indicator dots */}
            <div className="flex items-center gap-2 mt-8" data-testid="hero-dots">
              {HERO_IMAGES.map((img, i) => (
                <button
                  key={img.url}
                  type="button"
                  onClick={() => setHeroIndex(i)}
                  aria-label={`Show ${img.label}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === heroIndex ? "w-10 bg-amber-500" : "w-4 bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Photo credit (when present) */}
        {currentHero.credit && (
          <div className="absolute bottom-3 right-4 z-10 text-[10px] text-white/40 font-mono" data-testid="hero-credit">
            {currentHero.credit}
          </div>
        )}
      </section>

      {/* Stats Bar - Glowing numbers */}
      {stats && (
        <section className="relative bg-[#0d0d0d] border-y border-white/5 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-orange-500/5" />
          <div className="max-w-7xl mx-auto px-4 py-10 relative">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              <div className="text-center" data-testid="stat-depots">
                <p className="font-mono text-4xl md:text-5xl font-bold stat-number">
                  {stats.total_depots.toLocaleString()}
                </p>
                <p className="text-sm text-white/50 mt-2 uppercase tracking-wider">Depots Mapped</p>
              </div>
              <div className="text-center" data-testid="stat-towers">
                <p className="font-mono text-4xl md:text-5xl font-bold text-amber-400">
                  {(stats.total_towers || 0).toLocaleString()}
                </p>
                <p className="text-sm text-white/50 mt-2 uppercase tracking-wider">Towers Mapped</p>
              </div>
              <div className="text-center" data-testid="stat-states">
                <p className="font-mono text-4xl md:text-5xl font-bold text-white">
                  {stats.states_count}
                </p>
                <p className="text-sm text-white/50 mt-2 uppercase tracking-wider">States & Provinces</p>
              </div>
              <div className="text-center" data-testid="stat-photos">
                <p className="font-mono text-4xl md:text-5xl font-bold text-white">
                  {stats.depots_with_photos.toLocaleString()}
                </p>
                <p className="text-sm text-white/50 mt-2 uppercase tracking-wider">With Photos</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Search & Filter Bar */}
      <section className="sticky top-16 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/5 py-5">
        <div className="max-w-7xl mx-auto px-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                type="text"
                placeholder="Search depots by name or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-500/50 focus:ring-amber-500/20"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-full md:w-[200px] h-12 bg-white/5 border-white/10 text-white" data-testid="state-filter">
                <SelectValue placeholder="All States & Provinces" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 z-[100] max-h-[300px]">
                <SelectItem value="all">All States & Provinces</SelectItem>
                {states.map((state) => (
                  <SelectItem key={state.state_code} value={state.state_code}>
                    {state.state} ({state.depot_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full md:w-[160px] h-12 bg-white/5 border-white/10 text-white" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 z-[100]">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="original">Original Site</SelectItem>
                <SelectItem value="relocated">Relocated</SelectItem>
              </SelectContent>
            </Select>

            <Button type="submit" className="h-12 px-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>

          <div className="max-w-5xl mx-auto mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setOnlyVisitable(v => !v)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-medium transition-colors ${
                onlyVisitable
                  ? "bg-amber-500 text-black border border-amber-500"
                  : "bg-white/5 text-white/80 border border-white/15 hover:border-amber-500/50 hover:text-white"
              }`}
              data-testid="filter-visitable-btn"
            >
              {onlyVisitable ? "✓ Visitable museums only" : "Visitable museums"}
            </button>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="map-section" className="py-10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-serif text-3xl font-bold text-white">
                {loading ? "Loading..." : (
                  <>
                    {showDepots && <span>{depots.length.toLocaleString()} Depots</span>}
                    {showDepots && showTowers && <span className="text-white/30 mx-2">·</span>}
                    {showTowers && <span>{towers.length.toLocaleString()} Towers</span>}
                  </>
                )}
              </h2>
              <p className="text-white/50 mt-1">Click markers for details • Use filters and toggles to narrow results</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showMap ? "default" : "outline"}
                size="sm"
                onClick={() => setShowMap(true)}
                className={showMap ? "bg-amber-500 text-black hover:bg-amber-600" : "border-white/20 text-white hover:bg-white/5"}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4 mr-1" />
                Map
              </Button>
              <Button
                variant={!showMap ? "default" : "outline"}
                size="sm"
                onClick={() => setShowMap(false)}
                className={!showMap ? "bg-amber-500 text-black hover:bg-amber-600" : "border-white/20 text-white hover:bg-white/5"}
                data-testid="view-list-btn"
              >
                List
              </Button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50" />
              <span className="text-white/70">Depot · original (with photo)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50" />
              <span className="text-white/70">Depot · original (no photo)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50" />
              <span className="text-white/70">Depot · relocated</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rotate-45 bg-amber-500 shadow-lg shadow-amber-500/50" />
              <span className="text-white/70">Tower</span>
            </div>

            <div className="ml-auto flex items-center gap-5">
              <div className="flex items-center gap-2">
                <Switch
                  id="toggle-depots"
                  checked={showDepots}
                  onCheckedChange={setShowDepots}
                  data-testid="toggle-depots"
                />
                <Label htmlFor="toggle-depots" className="text-white/70 cursor-pointer text-sm">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Depots
                  </span>
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="toggle-towers"
                  checked={showTowers}
                  onCheckedChange={setShowTowers}
                  data-testid="toggle-towers"
                />
                <Label htmlFor="toggle-towers" className="text-white/70 cursor-pointer text-sm">
                  <span className="inline-flex items-center gap-1">
                    <Radio className="w-3.5 h-3.5" /> Towers
                  </span>
                </Label>
              </div>
            </div>
          </div>

          {showMap ? (
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10 bg-[#111]">
              <div className="h-[550px] md:h-[650px]" data-testid="map-container">
                <DepotMap 
                  depots={depots}
                  towers={towers}
                  onDepotClick={handleDepotClick}
                  onTowerClick={handleTowerClick}
                  loading={loading}
                  darkMode={true}
                  showDepots={showDepots}
                  showTowers={showTowers}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="depot-list">
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <Card key={i} className="bg-white/5 border-white/10 animate-pulse">
                    <CardContent className="p-5">
                      <div className="h-5 bg-white/10 rounded w-3/4 mb-3" />
                      <div className="h-4 bg-white/10 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))
              ) : (
                depots.map((depot) => (
                  <Card 
                    key={depot.id} 
                    className="depot-card cursor-pointer bg-[#111] border-white/5 hover:bg-[#151515]"
                    onClick={() => handleDepotClick(depot)}
                    data-testid={`depot-card-${depot.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-serif font-semibold text-lg line-clamp-1 text-white">
                          {depot.name}
                        </h3>
                        <Badge 
                          variant={depot.status === "original" ? "default" : "secondary"}
                          className={`ml-2 shrink-0 ${depot.status === "original" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30"}`}
                        >
                          {depot.status}
                        </Badge>
                      </div>
                      <p className="text-white/50 text-sm mb-3">
                        {depot.city}, {depot.state}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        {depot.railroad && (
                          <span className="text-white/40 flex items-center gap-1">
                            <Train className="w-3 h-3" />
                            {depot.railroad}
                          </span>
                        )}
                        {depot.has_photo && (
                          <span className="text-amber-500 flex items-center gap-1">
                            <Camera className="w-3 h-3" />
                            Photos
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured trips carousel */}
      <FeaturedTrips />

      {/* Quick Links - Show ALL states */}
      <section className="py-16 bg-[#0d0d0d] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold mb-8 text-white">Browse by State/Province</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {states.map((state) => (
              <Link
                key={state.state_code}
                to={`/states/${state.state_code}`}
                className="group p-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-amber-500/10 hover:border-amber-500/30 transition-all text-center"
                data-testid={`state-link-${state.state_code}`}
              >
                <p className="font-bold text-white group-hover:text-amber-500 transition-colors">{state.state_code}</p>
                <p className="text-xs text-white/40">{state.depot_count}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <DepotSeal size={44} className="text-amber-500 shrink-0" showEst={false} />
                <div>
                  <span className="font-serif font-bold block text-white leading-tight">North American Depots &amp; Towers</span>
                  <span className="text-xs text-amber-500">Hosted by Railstream</span>
                </div>
              </div>
              <p className="text-sm text-white/60">
                Preserving the history of North America&apos;s railroad passenger depots and interlocking towers for future generations.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-3 text-sm text-white/40">
                <li><Link to="/" className="hover:text-amber-500 transition-colors">Explore Map</Link></li>
                <li><Link to="/states" className="hover:text-amber-500 transition-colors">Browse States</Link></li>
                <li><Link to="/about" className="hover:text-amber-500 transition-colors">About This Project</Link></li>
                <li><Link to="/admin" className="hover:text-amber-500 transition-colors">Admin Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">Data Attribution</h4>
              <p className="text-sm text-white/40">
                Depot data originally compiled by <span className="text-amber-500">Jack Marshall</span> at{" "}
                <a href="https://www.depotmaps.com" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">
                  depotmaps.com
                </a>
                . Tower data from{" "}
                <a href="https://northamericaninterlockings.com" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">
                  North American Interlockings
                </a>
                . Canadian depot data from heritage railway records.
              </p>
              <div className="mt-4 pt-4 border-t border-white/5">
                <h5 className="font-semibold text-sm text-white mb-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-amber-500" />
                  Copyright &amp; Takedown
                </h5>
                <p className="text-xs text-white/40 leading-relaxed">
                  This is a non-commercial preservation archive. If you own the rights to any image
                  here and want it removed or recredited, we respond within 48 hours.
                </p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  <Link to="/legal/takedown" className="text-amber-500 hover:underline" data-testid="footer-takedown-policy">
                    Takedown policy
                  </Link>
                  <span className="text-white/20">·</span>
                  <a href="mailto:takedown@railstream.net" className="text-amber-500 hover:underline" data-testid="footer-takedown-email">
                    takedown@railstream.net
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 text-center text-sm text-white/30">
            <p>© 2026 Railstream • In tribute to Jack Marshall&apos;s decades of preserving railroad history</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
